/**
 * ruleSchema.js — Unified rule model for fx-validator.
 *
 * Before this, two incompatible rule shapes existed:
 *   1. RuleEngine.js KB rules:   { parameter, expectedPattern, expectedValue, severity:'required'|'recommended'|'info' }
 *   2. Profile cust_params rules: { key, pages, required, severity:'fail'|'warn', pattern, values, multi }
 *
 * Neither was editable from the UI. This module defines ONE superset rule shape,
 * a normalizer that ingests either legacy shape, and a single evaluator. The
 * RuleSetEditor UI, the per-request validators, and the regression runner all
 * speak this one schema.
 *
 * A Rule:
 * {
 *   id:              string   // stable unique id
 *   label:           string
 *   enabled:         boolean  // default true
 *   builtin:         boolean  // true = ships with the tool, cannot be deleted (only disabled)
 *   requestTypes:    string[] // ['google','prebid','amazon','ima','amazon_video','springserve_vast', ...] or ['*']
 *   parameter:       string   // dot-path: 'cust_params.amzn_b', 'imp[0].ext.prebid.storedrequest.id', or a url query key like 'iu'
 *   op:              'present'|'absent'|'equals'|'oneOf'|'matches'|'notMatches'|'gt'|'lt'|'range'
 *   expectedValue:   string|number   // equals / gt / lt
 *   values:          string[]        // oneOf
 *   expectedPattern: string          // matches / notMatches  (JS regex source)
 *   range:           [number,number] // range (inclusive)
 *   multi:           boolean         // value is a multi-token list (comma / & separated); every token must satisfy op
 *   pages:           string[]        // page scope (iu path segment or pageType cust_param). empty = all pages
 *   severity:        'fail'|'warn'|'info'
 *   failMessage:     string
 *   passMessage:     string
 * }
 *
 * Evaluation result (compatible with validators.jsx groups[].results[]):
 * {
 *   id, label, severity:'fail'|'warn'|'pass', message,
 *   _ruleDriven:true, _parameter, _value, _skipped?
 * }
 */

export const OPS = ['present', 'absent', 'equals', 'oneOf', 'matches', 'notMatches', 'gt', 'lt', 'range'];
export const SEVERITIES = ['fail', 'warn', 'info'];

let _seq = 0;
/** Generate a stable-ish unique rule id. */
export function newRuleId(prefix = 'rule') {
  _seq += 1;
  return `${prefix}_${Date.now().toString(36)}_${_seq.toString(36)}`;
}

/* ─── Low-level value readers (self-contained so this module has no deps) ──────── */

function parseQS(url = '') {
  const out = {};
  try {
    const i = url.indexOf('?');
    if (i < 0) return out;
    for (const pair of url.slice(i + 1).split('&')) {
      const eq = pair.indexOf('=');
      if (eq < 0) continue;
      const k = safeDecode(pair.slice(0, eq));
      const v = safeDecode(pair.slice(eq + 1));
      if (out[k] !== undefined) out[k] = Array.isArray(out[k]) ? [...out[k], v] : [out[k], v];
      else out[k] = v;
    }
  } catch {}
  return out;
}

function parseKV(s = '') {
  const out = {};
  try {
    for (const pair of String(s).split('&')) {
      const eq = pair.indexOf('=');
      if (eq < 0) continue;
      out[safeDecode(pair.slice(0, eq))] = safeDecode(pair.slice(eq + 1));
    }
  } catch {}
  return out;
}

function safeDecode(s) {
  try { return decodeURIComponent(String(s).replace(/\+/g, ' ')); } catch { return s; }
}

function tryBody(req) {
  const body = req?.requestBodyParsed || req?.requestBody || req?.body || req?.validData?.raw;
  if (!body) return null;
  try { return typeof body === 'object' ? body : JSON.parse(body); } catch { return null; }
}

function resolvePath(obj, path) {
  if (!obj || !path) return undefined;
  const parts = String(path).replace(/\[(\d+)\]/g, '.$1').split('.');
  let cur = obj;
  for (const part of parts) {
    if (cur === null || cur === undefined) return undefined;
    if (Array.isArray(cur)) {
      const idx = Number(part);
      cur = Number.isNaN(idx) ? cur[0]?.[part] : cur[idx];
    } else {
      cur = cur[part];
    }
  }
  return cur;
}

/**
 * Read a parameter value from a request, trying the common locations in order:
 *   1. cust_params.<key>  — decoded from the GAM url's cust_params blob
 *   2. url query string   — raw query key (iu, sz, correlator, ...)
 *   3. validData          — proxy pre-parsed fields
 *   4. parsed JSON body    — OpenRTB / APS bodies (dot-path)
 * Returns undefined when absent.
 */
export function readParam(req, parameter) {
  if (!req || !parameter) return undefined;
  const url = req.url || '';

  // cust_params.<key>
  if (parameter.startsWith('cust_params.')) {
    const key = parameter.slice('cust_params.'.length);
    const qs = parseQS(url);
    const cust = parseKV(qs.cust_params || req?.validData?.cust_params || '');
    if (cust[key] !== undefined) return cust[key];
    // some GAM urls double-encode cust_params
    const cust2 = parseKV(safeDecode(qs.cust_params || ''));
    return cust2[key];
  }

  // url query key (no dots → likely a query param)
  if (!parameter.includes('.') && !parameter.includes('[')) {
    const qs = parseQS(url);
    if (qs[parameter] !== undefined) return qs[parameter];
  }

  // validData
  const vd = resolvePath(req.validData, parameter);
  if (vd !== undefined) return vd;

  // body
  return resolvePath(tryBody(req), parameter);
}

/* ─── Normalizers (legacy → unified) ─────────────────────────────────────────── */

/** Map legacy KB severity vocab to unified. */
function mapKbSeverity(s) {
  if (s === 'required') return 'fail';
  if (s === 'recommended') return 'warn';
  return 'info';
}

/**
 * Ingest a cust_params rule (profile / seed shape):
 *   { key, label, pages, required, severity:'fail'|'warn', pattern, values, multi }
 * Cust params live on GAM (google) and IMA video requests.
 */
export function fromCustParamRule(r, idx = 0) {
  const op = r.values && r.values.length ? 'oneOf'
           : r.pattern ? 'matches'
           : 'present';
  return tidy({
    id:           r.id || `cp_${r.key}_${(r.pages || []).join('-') || 'all'}_${idx}`,
    label:        r.label || r.key,
    enabled:      r.enabled !== false,
    builtin:      !!r.builtin,
    requestTypes: r.requestTypes || ['google', 'ima'],
    parameter:    `cust_params.${r.key}`,
    op,
    expectedValue: r.expectedValue,
    values:        r.values,
    expectedPattern: r.pattern,
    multi:         !!r.multi,
    pages:         r.pages || [],
    // 'required' upgrades a warn to fail when the param is missing; the seed shape
    // carries both `required` and `severity` — severity is the violation severity.
    severity:      r.severity || (r.required ? 'fail' : 'warn'),
    _required:     r.required !== false && r.required !== undefined ? !!r.required : undefined,
    failMessage:   r.failMessage,
    passMessage:   r.passMessage,
  });
}

/**
 * Ingest a KB requirement (RuleEngine shape):
 *   { id, name, severity:'required'|'recommended'|'info', parameter, expectedPattern, expectedValue, ... }
 */
export function fromKbRequirement(r, requestTypes) {
  const op = r.expectedValue !== undefined ? 'equals'
           : r.expectedPattern ? 'matches'
           : 'present';
  return tidy({
    id:           r.id || newRuleId('kb'),
    label:        r.name || r.id,
    enabled:      true,
    builtin:      true,
    requestTypes: requestTypes || r.appliesTo || ['*'],
    parameter:    r.parameter,
    op,
    expectedValue: r.expectedValue,
    expectedPattern: r.expectedPattern,
    pages:         [],
    severity:      mapKbSeverity(r.severity),
    failMessage:   r.failMessage,
    passMessage:   r.passMessage,
  });
}

/**
 * Accept any of: unified rule, cust_params rule, KB requirement.
 * Heuristic: a unified rule has an `op`; a cust_params rule has `key`; a KB
 * requirement has `name` + `parameter` but no `op`.
 */
export function normalizeRule(raw, idx = 0) {
  if (!raw || typeof raw !== 'object') return null;
  if (raw.op && raw.parameter) return tidy(raw);          // already unified
  if (raw.key !== undefined) return fromCustParamRule(raw, idx);
  if (raw.name !== undefined || raw.parameter !== undefined) return fromKbRequirement(raw);
  return null;
}

/** Normalize an array, dropping nulls. */
export function normalizeRuleSet(arr = []) {
  return (Array.isArray(arr) ? arr : []).map(normalizeRule).filter(Boolean);
}

function tidy(rule) {
  const out = { ...rule };
  if (out.enabled === undefined) out.enabled = true;
  if (out.builtin === undefined) out.builtin = false;
  if (!Array.isArray(out.requestTypes)) out.requestTypes = out.requestTypes ? [out.requestTypes] : ['*'];
  if (!Array.isArray(out.pages)) out.pages = [];
  if (!out.severity) out.severity = 'warn';
  if (!out.op) out.op = 'present';
  return out;
}

/* ─── Page scoping ───────────────────────────────────────────────────────────── */

/**
 * Decide which "page" a GAM/IMA request belongs to, so page-scoped rules apply.
 * Two strategies (matches the existing validators.jsx behaviour):
 *   - pageType cust_param (some apps have an explicit pageType)
 *   - iu path segment     (some apps use the ad-unit path: /4145/generic/hp/...)
 */
export function pageIdentity(req) {
  const url = req?.url || '';
  const qs = parseQS(url);
  const cust = parseKV(qs.cust_params || req?.validData?.cust_params || '');
  const pageType = cust.pageType || cust.pagetype || cust.pn;
  const iu = qs.iu || req?.validData?.iu || '';
  const seg = String(iu).split('/').filter(Boolean);
  // segment after the publisher (network/brand/platform/PAGE/...)
  const iuPage = seg.length >= 4 ? seg[3] : (seg[seg.length - 2] || '');
  return { pageType: pageType || null, iuPage: iuPage || null };
}

/** Does a rule's page scope match this request? Empty scope = always applies. */
export function ruleAppliesToPage(rule, req) {
  if (!rule.pages || rule.pages.length === 0) return true;
  const { pageType, iuPage } = pageIdentity(req);
  return rule.pages.some(p => p === pageType || p === iuPage);
}

/** Does a rule apply to this request type? */
export function ruleAppliesToType(rule, requestType) {
  if (!rule.requestTypes || rule.requestTypes.includes('*')) return true;
  return rule.requestTypes.includes(requestType);
}

/* ─── Evaluation ─────────────────────────────────────────────────────────────── */

function tokens(value, multi) {
  if (value === undefined || value === null) return [];
  if (Array.isArray(value)) return value.map(String);
  const s = String(value);
  if (!multi) return [s];
  // multi-token: GAM cust_params use comma; some use & or pipe
  return s.split(/[,|&]/).map(t => t.trim()).filter(Boolean);
}

function opSatisfied(op, rule, tok) {
  switch (op) {
    case 'present': return tok !== undefined && tok !== '';
    case 'absent':  return tok === undefined || tok === '';
    case 'equals':  return String(tok) === String(rule.expectedValue);
    case 'oneOf':   return (rule.values || []).map(String).includes(String(tok));
    case 'matches':
      try { return new RegExp(rule.expectedPattern).test(String(tok)); } catch { return true; }
    case 'notMatches':
      try { return !new RegExp(rule.expectedPattern).test(String(tok)); } catch { return true; }
    case 'gt': return Number(tok) > Number(rule.expectedValue);
    case 'lt': return Number(tok) < Number(rule.expectedValue);
    case 'range': {
      const [lo, hi] = rule.range || [];
      const n = Number(tok);
      return n >= Number(lo) && n <= Number(hi);
    }
    default: return true;
  }
}

/**
 * Evaluate one rule against one request.
 * Returns a result object, or null if the rule does not apply to this
 * request type / page (so the caller can skip it cleanly).
 */
export function evaluateRule(rule, req, requestType) {
  if (!rule || rule.enabled === false) return null;
  if (!ruleAppliesToType(rule, requestType)) return null;
  if (!ruleAppliesToPage(rule, req)) return null;

  const sev = rule.severity === 'info' ? 'pass' : rule.severity; // info → shown green
  const value = readParam(req, rule.parameter);
  const present = value !== undefined && value !== null && value !== '';

  // 'absent' is the only op that wants the param to NOT be there.
  if (rule.op === 'absent') {
    const passed = !present;
    return result(rule, passed ? 'pass' : sev, value,
      passed ? (rule.passMessage || `${rule.label} — correctly absent`)
             : (rule.failMessage || `${rule.label} — should be absent but is present`));
  }

  // For everything else, a missing param is a failure (respecting severity).
  if (!present) {
    return result(rule, sev, value,
      rule.failMessage || `${rule.label} — "${rule.parameter}" not found`);
  }

  // present-only check passes here
  if (rule.op === 'present') {
    return result(rule, 'pass', value, rule.passMessage || `${rule.label} — present`);
  }

  // value-based ops: every token must satisfy
  const toks = tokens(value, rule.multi);
  const bad = toks.filter(t => !opSatisfied(rule.op, rule, t));
  if (bad.length) {
    return result(rule, sev, value,
      rule.failMessage || `${rule.label} — ${describeMismatch(rule, bad)}`);
  }
  return result(rule, 'pass', value, rule.passMessage || `${rule.label} — ${shortVal(value)}`);
}

function describeMismatch(rule, bad) {
  const got = bad.join(', ');
  if (rule.op === 'oneOf')   return `"${got}" not in [${(rule.values || []).join(', ')}]`;
  if (rule.op === 'equals')  return `expected "${rule.expectedValue}", got "${got}"`;
  if (rule.op === 'matches') return `"${got}" does not match /${rule.expectedPattern}/`;
  if (rule.op === 'notMatches') return `"${got}" must not match /${rule.expectedPattern}/`;
  if (rule.op === 'range')   return `"${got}" outside [${(rule.range || []).join('..')}]`;
  return `"${got}" failed ${rule.op}`;
}

function shortVal(v) {
  const s = Array.isArray(v) ? v.join(', ') : String(v);
  return s.length > 80 ? s.slice(0, 80) + '…' : s;
}

function result(rule, severity, value, message) {
  return {
    id:        rule.id,
    label:     rule.label,
    severity,
    message,
    _ruleDriven: true,
    _parameter:  rule.parameter,
    _value:      value === undefined ? undefined : shortVal(value),
  };
}

/**
 * Run a whole ruleset against one request → a validators.jsx-compatible group.
 * Only rules applicable to the request's type/page produce results.
 */
export function runRuleSet(ruleSet, req, requestType, title = 'Custom Rules') {
  const rules = normalizeRuleSet(ruleSet);
  const results = rules.map(r => evaluateRule(r, req, requestType)).filter(Boolean);
  if (!results.length) return null;
  return { title, scope: 'RULES', results, _ruleDriven: true };
}
